import React, { useState, useEffect, useMemo } from "react";
import {
  MdClose,
  MdZoomIn,
  MdZoomOut,
  MdRefresh,
  MdNavigateBefore,
  MdNavigateNext,
  MdPhotoCamera,
  MdFactCheck,
  MdCheckCircle,
  MdCancel,
} from "react-icons/md";

export interface VisualCheckpointConfig {
  key: string;
  label: string;
  imageSrc: string;
  isCrop?: boolean;
  cropRect?: { x: number; y: number; w: number; h: number };
  criteria: string;
}

export const VISUAL_CHECKPOINT_CONFIGS: VisualCheckpointConfig[] = [
  {
    key: "gooseneck",
    label: "Gooseneck Cover Direction",
    imageSrc: "/iqc_reference/gooseneck_cover_direction.png",
    criteria: "Verify orientation and snug fit of rubber gooseneck covers. Sleeves must face outward correctly with no tears.",
  },
  {
    key: "batteryPcb",
    label: "Battery & Battery PCB Soldering Point",
    imageSrc: "/iqc_reference/battery_soldering_point.png",
    criteria: "Inspect red (+) & black (-) battery wire solder joints. Soldering must be shiny, clean, fully bonded without dry joints or bridging.",
  },
  {
    key: "buttonPcba",
    label: "Button PCBA Soldering Check Point",
    imageSrc: "/iqc_reference/button_pcba_soldering.png",
    criteria: "Check button PCBA solder joints, Type-C charging port pins, and button tactile switches. Wire routing must be secure.",
  },
  {
    key: "glue",
    label: "Glue Check Point",
    imageSrc: "/iqc_reference/glue_check_point.png",
    criteria: "Verify white silicone sealant / potting glue covers wire solder points completely to ensure strain relief and moisture sealing.",
  },
  {
    key: "macSticker",
    label: "MAC ID Sticker",
    imageSrc: "/iqc_reference/visual_inspection_reference_table.png",
    isCrop: true,
    cropRect: { x: 326, y: 597, w: 417, h: 103 },
    criteria: "Inspect QR code & MAC sticker label on PCB. Barcode / QR matrix and human-readable text must be clean, crisp, and firmly attached.",
  },
  {
    key: "speakerWire",
    label: "Speaker Wire",
    imageSrc: "/iqc_reference/visual_inspection_reference_table.png",
    isCrop: true,
    cropRect: { x: 326, y: 701, w: 417, h: 183 },
    criteria: "Inspect circular speaker transducer, wire solder termination, black mesh protective layer, and cable strain relief.",
  },
];

// Helper hook to cache and generate cropped images for Row 5 and Row 6 using offscreen canvas
export function useVisualReferenceImages() {
  const [resolvedImages, setResolvedImages] = useState<Record<string, string>>({
    gooseneck: "/iqc_reference/gooseneck_cover_direction.png",
    batteryPcb: "/iqc_reference/battery_soldering_point.png",
    buttonPcba: "/iqc_reference/button_pcba_soldering.png",
    glue: "/iqc_reference/glue_check_point.png",
    macSticker: "/iqc_reference/visual_inspection_reference_table.png",
    speakerWire: "/iqc_reference/visual_inspection_reference_table.png",
    fullTable: "/iqc_reference/visual_inspection_reference_table.png",
  });

  useEffect(() => {
    let isMounted = true;
    const tableImg = new Image();
    tableImg.crossOrigin = "anonymous";
    tableImg.src = "/iqc_reference/visual_inspection_reference_table.png";

    tableImg.onload = () => {
      if (!isMounted) return;
      const updates: Record<string, string> = {};

      VISUAL_CHECKPOINT_CONFIGS.forEach((cfg) => {
        if (cfg.isCrop && cfg.cropRect) {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = cfg.cropRect.w;
            canvas.height = cfg.cropRect.h;
            const ctx = canvas.getContext("2d");
            if (ctx) {
              ctx.drawImage(
                tableImg,
                cfg.cropRect.x,
                cfg.cropRect.y,
                cfg.cropRect.w,
                cfg.cropRect.h,
                0,
                0,
                cfg.cropRect.w,
                cfg.cropRect.h
              );
              updates[cfg.key] = canvas.toDataURL("image/png");
            }
          } catch (e) {
            console.warn("Could not crop canvas for " + cfg.key, e);
          }
        }
      });

      if (Object.keys(updates).length > 0) {
        setResolvedImages((prev) => ({ ...prev, ...updates }));
      }
    };

    return () => {
      isMounted = false;
    };
  }, []);

  return resolvedImages;
}

// Inline Thumbnail component for checkpoint rows
export const IqcCheckpointThumbnail: React.FC<{
  checkpointKey: string;
  onClick: () => void;
  resolvedSrc?: string;
}> = ({ checkpointKey, onClick, resolvedSrc }) => {
  const config = VISUAL_CHECKPOINT_CONFIGS.find((c) => c.key === checkpointKey);
  const src = resolvedSrc || config?.imageSrc || "/iqc_reference/visual_inspection_reference_table.png";

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      title={`Click to enlarge reference picture for ${config?.label || "Checkpoint"}`}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        padding: "2px 6px",
        borderRadius: "6px",
        backgroundColor: "#f8fafc",
        border: "1px solid #cbd5e1",
        cursor: "pointer",
        transition: "all 0.15s ease",
        userSelect: "none",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = "#0284c7";
        e.currentTarget.style.backgroundColor = "#f0f9ff";
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(2, 132, 199, 0.2)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "#cbd5e1";
        e.currentTarget.style.backgroundColor = "#f8fafc";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div
        style={{
          width: "52px",
          height: "30px",
          borderRadius: "4px",
          overflow: "hidden",
          backgroundColor: "#0f172a",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          position: "relative",
        }}
      >
        <img
          src={src}
          alt={config?.label}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            objectPosition: "center",
            display: "block",
          }}
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).src =
              "/iqc_reference/visual_inspection_reference_table.png";
          }}
        />
      </div>
      <span
        style={{
          fontSize: "11px",
          fontWeight: 600,
          color: "#0284c7",
          display: "flex",
          alignItems: "center",
          gap: "3px",
          whiteSpace: "nowrap",
        }}
      >
        <MdPhotoCamera size={13} />
        <span>Ref Photo</span>
      </span>
    </div>
  );
};

// Full Lightbox Modal for zooming in on any reference picture
export const IqcReferenceLightboxModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  activeKey: string;
  onSelectKey: (key: string) => void;
  resolvedImages: Record<string, string>;
}> = ({ isOpen, onClose, activeKey, onSelectKey, resolvedImages }) => {
  const [zoomLevel, setZoomLevel] = useState<number>(1);

  useEffect(() => {
    setZoomLevel(1);
  }, [activeKey]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") handlePrev();
      if (e.key === "ArrowRight") handleNext();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  });

  if (!isOpen) return null;

  const currentIndex = Math.max(
    0,
    VISUAL_CHECKPOINT_CONFIGS.findIndex((c) => c.key === activeKey)
  );
  const currentConfig = VISUAL_CHECKPOINT_CONFIGS[currentIndex] || VISUAL_CHECKPOINT_CONFIGS[0];

  const handlePrev = () => {
    const prevIdx = (currentIndex - 1 + VISUAL_CHECKPOINT_CONFIGS.length) % VISUAL_CHECKPOINT_CONFIGS.length;
    onSelectKey(VISUAL_CHECKPOINT_CONFIGS[prevIdx].key);
  };

  const handleNext = () => {
    const nextIdx = (currentIndex + 1) % VISUAL_CHECKPOINT_CONFIGS.length;
    onSelectKey(VISUAL_CHECKPOINT_CONFIGS[nextIdx].key);
  };

  const activeSrc = resolvedImages[currentConfig.key] || currentConfig.imageSrc;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(15, 23, 42, 0.85)",
        backdropFilter: "blur(6px)",
        zIndex: 100000,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        padding: "20px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#ffffff",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "920px",
          maxHeight: "92vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.45)",
          border: "1px solid #cbd5e1",
        }}
      >
        {/* Header */}
        <div
          style={{
            background: "linear-gradient(135deg, #1e293b, #0f172a)",
            color: "#ffffff",
            padding: "14px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #334155",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "8px",
                backgroundColor: "#e60000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MdPhotoCamera size={20} color="#ffffff" />
            </div>
            <div>
              <h4 style={{ margin: 0, fontSize: "16px", fontWeight: 700, letterSpacing: "-0.2px" }}>
                {currentConfig.label}
              </h4>
              <div style={{ fontSize: "11.5px", color: "#94a3b8", marginTop: "2px" }}>
                Visual Checkpoint {currentIndex + 1} of 6 (Reference Standard)
              </div>
            </div>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "transparent",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
              }}
              title="Close (Esc)"
            >
              <MdClose size={24} />
            </button>
          </div>
        </div>

        {/* Thumbnail Selector Bar for all 6 Visual Checkpoints */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "10px 16px",
            background: "#f1f5f9",
            borderBottom: "1px solid #e2e8f0",
            overflowX: "auto",
          }}
        >
          {VISUAL_CHECKPOINT_CONFIGS.map((cfg, idx) => {
            const isSelected = cfg.key === activeKey;
            return (
              <button
                key={cfg.key}
                type="button"
                onClick={() => onSelectKey(cfg.key)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  padding: "6px 10px",
                  borderRadius: "8px",
                  border: isSelected ? "2px solid #e60000" : "1px solid #cbd5e1",
                  background: isSelected ? "#fee2e2" : "#ffffff",
                  color: isSelected ? "#991b1b" : "#334155",
                  fontSize: "11.5px",
                  fontWeight: isSelected ? 700 : 500,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                  transition: "all 0.15s ease",
                }}
              >
                <span
                  style={{
                    width: "18px",
                    height: "18px",
                    borderRadius: "50%",
                    backgroundColor: isSelected ? "#e60000" : "#64748b",
                    color: "#fff",
                    fontSize: "10px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                  }}
                >
                  {idx + 1}
                </span>
                <span>{cfg.label}</span>
              </button>
            );
          })}
        </div>

        {/* Image Preview Canvas Area */}
        <div
          style={{
            flex: 1,
            position: "relative",
            minHeight: "360px",
            maxHeight: "58vh",
            backgroundColor: "#0b0f19",
            overflow: "auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <img
            src={activeSrc}
            alt={currentConfig.label}
            style={{
              maxWidth: "95%",
              maxHeight: "52vh",
              objectFit: "contain",
              transform: `scale(${zoomLevel})`,
              transformOrigin: "center center",
              transition: "transform 0.15s ease",
              borderRadius: "8px",
              boxShadow: "0 10px 30px rgba(0, 0, 0, 0.5)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          />

          {/* Left / Right Nav Arrows */}
          <button
            type="button"
            onClick={handlePrev}
            title="Previous Checkpoint"
            style={{
              position: "absolute",
              left: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(15, 23, 42, 0.75)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
            }}
          >
            <MdNavigateBefore size={26} />
          </button>
          <button
            type="button"
            onClick={handleNext}
            title="Next Checkpoint"
            style={{
              position: "absolute",
              right: "14px",
              top: "50%",
              transform: "translateY(-50%)",
              background: "rgba(15, 23, 42, 0.75)",
              color: "#ffffff",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              backdropFilter: "blur(4px)",
            }}
          >
            <MdNavigateNext size={26} />
          </button>

          {/* Floating Zoom Controls Bar */}
          <div
            style={{
              position: "absolute",
              bottom: "14px",
              right: "18px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              background: "rgba(15, 23, 42, 0.8)",
              padding: "4px 8px",
              borderRadius: "8px",
              border: "1px solid rgba(255, 255, 255, 0.2)",
              backdropFilter: "blur(4px)",
            }}
          >
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.max(0.6, z - 0.2))}
              style={{
                background: "transparent",
                border: "none",
                color: "#ffffff",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
              }}
              title="Zoom Out"
            >
              <MdZoomOut size={18} />
            </button>
            <span style={{ color: "#cbd5e1", fontSize: "11px", fontWeight: 600, minWidth: "38px", textAlign: "center" }}>
              {Math.round(zoomLevel * 100)}%
            </span>
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.min(2.5, z + 0.2))}
              style={{
                background: "transparent",
                border: "none",
                color: "#ffffff",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
              }}
              title="Zoom In"
            >
              <MdZoomIn size={18} />
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel(1)}
              style={{
                background: "transparent",
                border: "none",
                color: "#94a3b8",
                cursor: "pointer",
                padding: "4px",
                display: "flex",
              }}
              title="Reset Zoom"
            >
              <MdRefresh size={18} />
            </button>
          </div>
        </div>

        {/* Quality Standard Criteria Description Footer */}
        <div
          style={{
            padding: "14px 20px",
            background: "#f8fafc",
            borderTop: "1px solid #e2e8f0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
          }}
        >
          <div style={{ flex: 1, minWidth: "260px" }}>
            <div style={{ fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase" }}>
              Inspection Quality Standard
            </div>
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#1e293b", marginTop: "2px" }}>
              {currentConfig.criteria}
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                background: "#0f172a",
                color: "#ffffff",
                border: "none",
                borderRadius: "8px",
                padding: "8px 18px",
                fontSize: "12.5px",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Done & Return to Inspection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
